import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Calendar, Search, RotateCcw, Mail, ArrowLeft } from 'lucide-react';

import { useKpisCuadre } from './hooks/useKpisCuadre';
import { useAbortados } from './hooks/useAbortados';
import { useParticipacion } from './hooks/useParticipacion';
import { useDetalleDiferencias } from './hooks/useDetalleDiferencias';
import { useDetergentesQuimicos } from './hooks/useDetergentesQuimicos';
import { useControlDiario } from './hooks/useControlDiario';
import { useConsolidadosAnio } from './hooks/useConsolidadosAnio';
import { buildDateRange } from './services/reportesV2.services';
import useCentrosPermitidos from '@/hooks/useCentrosPermitidos';
import { getReportById, getEnabledReports } from './reportsRegistry';
import LotesVsCiclosContent from './sections/LotesVsCiclosContent';
import DetergentesQuimicosContent from './sections/detergentes/DetergentesQuimicosContent';
import RecepcionRopaSuciaContent from './sections/registros/RecepcionRopaSuciaContent';
import AlistamientosContent from './sections/registros/AlistamientosContent';
import ControlDiarioContent from './sections/controlDiario/ControlDiarioContent';
import ConsolidadoAnualContent from './sections/consolidadoAnual/ConsolidadoAnualContent';
import NominaCentroContent from './sections/nomina/NominaCentroContent';
import AnalisisNominaContent from './sections/nomina/AnalisisNominaContent';
import SendReportDialog from './components/SendReportDialog';
import PlantasSelector from './components/PlantasSelector';

const REPORT_IDS_IMPLEMENTADOS = ['lotes-vs-ciclos', 'consumo-productos', 'recepcion-ropa-sucia', 'alistamientos-registros', 'control-diario-produccion', 'consolidado-anual-produccion', 'nomina-centro', 'analisis-nomina'];

const toISODate = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString();
const dateInputToLocalISO = (value) => {
  if (!value) return null;
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d).toISOString();
};

const Reportes360Page = () => {
  const navigate = useNavigate();
  const { puedeVerCentro, cargarCentros, centroPorDefecto } = useCentrosPermitidos();
  // Default: primer centro que el usuario pueda ver (su lavandería si no hay
  // centros asignados, o si está entre ellos)
  const idLavanderiaInicial = centroPorDefecto;
  const [laundries, setLaundries] = useState([]);

  // === Estado: tipo de reporte ===
  const enabledReports = getEnabledReports();
  const [selectedReportId, setSelectedReportId] = useState(enabledReports[0]?.id || 'lotes-vs-ciclos');
  const selectedReport = useMemo(() => getReportById(selectedReportId), [selectedReportId]);

  // === Estado del FORM (lo que ve el usuario, no dispara carga) ===
  const today = new Date();
  const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
  const firstDayCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const firstDayPrevMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const defaultStart = yesterday < firstDayCurrentMonth ? firstDayPrevMonth : firstDayCurrentMonth;

  const [formReportId, setFormReportId] = useState(selectedReportId);
  const [formIdLavanderia, setFormIdLavanderia] = useState(idLavanderiaInicial);
  // Reportes globales: plantas elegidas (vacío = todas).
  const [formPlantas, setFormPlantas] = useState([]);
  const [formStartDate, setFormStartDate] = useState(toISODate(defaultStart));
  const [formEndDate, setFormEndDate] = useState(toISODate(yesterday));
  // Reportes mensuales (p. ej. Control diario de producción): mes 'YYYY-MM'
  const defaultMonth = `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, '0')}`;
  const [formMonth, setFormMonth] = useState(defaultMonth);
  const isMonthly = !!selectedReport?.monthly;
  // Reportes anuales (p. ej. Consolidado anual de producción)
  const [formYear, setFormYear] = useState(today.getFullYear());
  const isYearly = !!selectedReport?.yearly;
  // Reportes globales (todas las plantas): no piden centro de lavado.
  const isGlobal = !!selectedReport?.global;
  const yearOptions = Array.from({ length: 5 }, (_, i) => today.getFullYear() - i);

  // === Estado APLICADO (lo que dispara la carga, solo cambia al dar "Buscar") ===
  const [applied, setApplied] = useState(null);

  // Cargar lista de lavanderías (acotada a los centros asignados, si los hay)
  useEffect(() => {
    cargarCentros()
      .then(setLaundries)
      .catch(() => setLaundries([]));
  }, [cargarCentros]);

  // Si cambia la lavandería del usuario, sincronizamos el form (no dispara carga)
  useEffect(() => {
    if (idLavanderiaInicial && !formIdLavanderia) {
      setFormIdLavanderia(idLavanderiaInicial);
    }
  }, [idLavanderiaInicial, formIdLavanderia]);

  const handleBuscar = useCallback(() => {
    setFormReportId(selectedReportId);
    if (isGlobal) {
      if (!formStartDate || !formEndDate) return;
      setApplied({
        idLavanderia: null,
        plantas: formPlantas,
        dateRange: buildDateRange(formStartDate, formEndDate),
        reportId: selectedReportId,
        startDate: formStartDate,
        endDate: formEndDate,
      });
      return;
    }
    if (!formIdLavanderia) return;
    if (isYearly) {
      const y = Number(formYear);
      const start = toISODate(new Date(y, 0, 1));
      const end = toISODate(new Date(y, 11, 31));
      setApplied({
        idLavanderia: formIdLavanderia,
        dateRange: buildDateRange(start, end),
        reportId: selectedReportId,
        startDate: start,
        endDate: end,
        year: y,
      });
      return;
    }
    if (isMonthly) {
      const [y, m] = String(formMonth || '').split('-').map(Number);
      if (!y || !m) return;
      const start = toISODate(new Date(y, m - 1, 1));
      const end = toISODate(new Date(y, m, 0));
      setApplied({
        idLavanderia: formIdLavanderia,
        dateRange: buildDateRange(start, end),
        reportId: selectedReportId,
        startDate: start,
        endDate: end,
        year: y,
        month: m,
      });
      return;
    }
    if (!formStartDate || !formEndDate) return;
    const range = buildDateRange(formStartDate, formEndDate);
    if (!range) return;
    setApplied({
      idLavanderia: formIdLavanderia,
      dateRange: range,
      reportId: selectedReportId,
      startDate: formStartDate,
      endDate: formEndDate,
    });
  }, [selectedReportId, formIdLavanderia, formStartDate, formEndDate, isMonthly, formMonth, isYearly, formYear, isGlobal, formPlantas]);

  const handleLimpiar = useCallback(() => {
    setFormStartDate(toISODate(defaultStart));
    setFormEndDate(toISODate(yesterday));
    setApplied(null);
  }, [defaultStart, yesterday]);

  const canApply = isGlobal
    ? !!formStartDate && !!formEndDate
    : !!formIdLavanderia &&
      puedeVerCentro(formIdLavanderia) &&
      (isYearly ? !!formYear : isMonthly ? !!formMonth : !!formStartDate && !!formEndDate);

  const currentLaundry = useMemo(
    () => laundries.find((l) => (l._id || l.id) === applied?.idLavanderia) || null,
    [laundries, applied?.idLavanderia],
  );
  const [sendDialogOpen, setSendDialogOpen] = useState(false);

  // Hooks: SOLO se disparan cuando `applied` cambia Y corresponden al reporte activo
  // (evita disparar fetches de un reporte mientras se está viendo otro).
  const isLotesVsCiclos = applied?.reportId === 'lotes-vs-ciclos';
  const isConsumoProductos = applied?.reportId === 'consumo-productos';

  const kpisCuadre = useKpisCuadre({
    idLavanderia: applied?.idLavanderia,
    dateRange: applied?.dateRange,
    enabled: !!applied && isLotesVsCiclos,
  });
  const abortados = useAbortados({
    idLavanderia: applied?.idLavanderia,
    dateRange: applied?.dateRange,
    enabled: !!applied && isLotesVsCiclos,
  });
  const participacion = useParticipacion({
    idLavanderia: applied?.idLavanderia,
    dateRange: applied?.dateRange,
    enabled: !!applied && isLotesVsCiclos,
  });
  const detalleDiferencias = useDetalleDiferencias({
    idLavanderia: applied?.idLavanderia,
    dateRange: applied?.dateRange,
    enabled: !!applied && isLotesVsCiclos,
  });
  const detergentes = useDetergentesQuimicos({
    idLavanderia: applied?.idLavanderia,
    dateRange: applied?.dateRange,
    enabled: !!applied && isConsumoProductos,
  });
  const isControlDiario = applied?.reportId === 'control-diario-produccion';
  const controlDiario = useControlDiario({
    idLavanderia: applied?.idLavanderia,
    dateRange: applied?.dateRange,
    enabled: !!applied && isControlDiario,
  });
  const isConsolidadoAnual = applied?.reportId === 'consolidado-anual-produccion';
  const consolidadosAnio = useConsolidadosAnio({
    idLavanderia: applied?.idLavanderia,
    year: applied?.year,
    enabled: !!applied && isConsolidadoAnual,
  });

  const hooks = { kpisCuadre, abortados, participacion, detalleDiferencias };
  const Icon = selectedReport?.icon;
  const toleranciaPct = currentLaundry?.indicadores?.detergentes?.porcentajeAceptacion;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-gray-200 dark:border-gray-700 pb-3 mb-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} aria-label="Volver">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-[27px] font-extrabold tracking-tight text-[#001F41] dark:text-white">
            Reportes específicos
          </h1>
          <p className="text-[13px] text-[#43474F] dark:text-gray-400 mt-1.5">
            Selecciona el tipo de reporte, el centro de lavado y el rango de fechas, luego haz clic en <b>Buscar</b>.
          </p>
        </div>
      </div>

      {/* Filtros */}
      <Card className="rounded-xl border border-gray-200/80 dark:border-gray-700/80 bg-white dark:bg-gray-800 shadow-sm">
        <CardContent className="p-4">
          {/* Fila 1: tipo de reporte + explicación */}
          <div className="flex flex-wrap items-end gap-x-4 gap-y-2 pb-4 mb-4 border-b border-gray-100 dark:border-gray-700">
            <div className="flex flex-col gap-1.5 min-w-[260px]">
              <Label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                1. Tipo de reporte
              </Label>
              <select
                className="h-10 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 text-sm"
                value={selectedReportId}
                onChange={(e) => setSelectedReportId(e.target.value)}
              >
                {enabledReports.map((r) => (
                  <option key={r.id} value={r.id}>{r.label}</option>
                ))}
              </select>
            </div>
            {selectedReport?.description && (
              <div className="flex items-center gap-2.5 h-10 flex-1 min-w-[240px]">
                {Icon && (
                  <div className="w-8 h-8 rounded-lg bg-[#E7EEF7] dark:bg-[#1a2a3f] flex items-center justify-center flex-none">
                    <Icon className="h-4 w-4 text-[#0F3460] dark:text-blue-200" />
                  </div>
                )}
                <p className="text-[13px] text-[#43474F] dark:text-gray-400">
                  {selectedReport.description}
                </p>
              </div>
            )}
          </div>

          {/* Fila 2: centro de lavado, fechas y acciones */}
          <div className="flex flex-wrap items-end gap-3">
            {/* 2) Centro de lavado (los reportes globales abarcan todas las plantas) */}
            {isGlobal ? (
              <div className="flex flex-col gap-1.5 min-w-[220px]">
                <Label className="text-xs font-medium text-gray-600 dark:text-gray-400">2. Plantas</Label>
                <PlantasSelector plantas={laundries} value={formPlantas} onChange={setFormPlantas} />
              </div>
            ) : (
            <div className="flex flex-col gap-1.5 min-w-[220px]">
              <Label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                2. Centro de Lavado
              </Label>
              <select
                className="h-10 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 text-sm"
                value={formIdLavanderia}
                onChange={(e) => setFormIdLavanderia(e.target.value)}
              >
                <option value="">Seleccione...</option>
                {laundries.map((l) => (
                  <option key={l._id || l.id} value={l._id || l.id}>{l.nombre}</option>
                ))}
              </select>
            </div>
            )}

            {/* 3) Fechas (o Mes / Año, en reportes mensuales / anuales) */}
            {isYearly ? (
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs font-medium text-gray-600 dark:text-gray-400">3. Año</Label>
                <select
                  className="h-10 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 text-sm min-w-[120px]"
                  value={formYear}
                  onChange={(e) => setFormYear(Number(e.target.value))}
                >
                  {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            ) : isMonthly ? (
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs font-medium text-gray-600 dark:text-gray-400">3. Mes</Label>
                <div className="relative">
                  <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="month"
                    className="h-10 pl-8 rounded-lg border-gray-200 dark:border-gray-700"
                    value={formMonth}
                    onChange={(e) => setFormMonth(e.target.value)}
                  />
                </div>
              </div>
            ) : (
            <>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-medium text-gray-600 dark:text-gray-400">3. Desde</Label>
              <div className="relative">
                <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="date"
                  className="h-10 pl-8 rounded-lg border-gray-200 dark:border-gray-700"
                  value={formStartDate.slice(0, 10)}
                  onChange={(e) => {
                    const iso = dateInputToLocalISO(e.target.value);
                    if (iso) setFormStartDate(iso);
                  }}
                />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-medium text-gray-600 dark:text-gray-400">Hasta</Label>
              <div className="relative">
                <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="date"
                  className="h-10 pl-8 rounded-lg border-gray-200 dark:border-gray-700"
                  value={formEndDate.slice(0, 10)}
                  onChange={(e) => {
                    const iso = dateInputToLocalISO(e.target.value);
                    if (iso) setFormEndDate(iso);
                  }}
                />
              </div>
            </div>
            </>
            )}

            {/* Botones Buscar / Limpiar */}
            <div className="flex gap-2 ml-auto">
              {applied && isLotesVsCiclos && (
                <Button
                  type="button"
                  onClick={() => setSendDialogOpen(true)}
                  className="h-10 bg-[#0F3460] hover:bg-[#001F41] text-white"
                >
                  <Mail className="h-4 w-4 mr-1.5" />
                  Enviar reporte por correo
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={handleLimpiar}
                disabled={!applied}
                className="h-10"
              >
                <RotateCcw className="h-4 w-4 mr-1.5" />
                Limpiar
              </Button>
              <Button
                type="button"
                onClick={handleBuscar}
                disabled={!canApply}
                className="h-10 bg-[#0F3460] hover:bg-[#001F41] text-white font-semibold"
              >
                <Search className="h-4 w-4 mr-1.5" />
                Buscar
              </Button>
            </div>
          </div>

          {!canApply && (
            <div className="mt-3 text-xs text-gray-500 dark:text-gray-400 italic">
              {!formIdLavanderia && !isGlobal
                ? 'Selecciona un centro de lavado para poder buscar.'
                : 'Completa las fechas para poder buscar.'}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Contenido: solo se muestra después de Buscar */}
      {!applied && canApply && (
        <Card className="rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50">
          <CardContent className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">
            <Search className="h-8 w-8 mx-auto mb-2 text-gray-300 dark:text-gray-600" />
            Listo. Haz clic en <b>Buscar</b> para cargar el reporte.
          </CardContent>
        </Card>
      )}

      {applied && isLotesVsCiclos && (
        <LotesVsCiclosContent
          hooks={hooks}
          dateRange={applied.dateRange}
          startDate={applied.startDate}
          endDate={applied.endDate}
        />
      )}

      {applied && isConsumoProductos && (
        <DetergentesQuimicosContent detergentes={detergentes} toleranciaPct={toleranciaPct} />
      )}

      {applied && applied.reportId === 'recepcion-ropa-sucia' && (
        <RecepcionRopaSuciaContent
          idLavanderia={applied.idLavanderia}
          dateRange={applied.dateRange}
          startDate={applied.startDate}
          endDate={applied.endDate}
        />
      )}

      {applied && applied.reportId === 'alistamientos-registros' && (
        <AlistamientosContent
          idLavanderia={applied.idLavanderia}
          dateRange={applied.dateRange}
          startDate={applied.startDate}
          endDate={applied.endDate}
        />
      )}

      {applied && applied.reportId === 'analisis-nomina' && (
        <AnalisisNominaContent startDate={applied.startDate} endDate={applied.endDate} plantasFiltro={applied.plantas || []} laundries={laundries} />
      )}

      {applied && applied.reportId === 'nomina-centro' && (
        <NominaCentroContent
          idLavanderia={applied.idLavanderia}
          startDate={applied.startDate}
          endDate={applied.endDate}
          laundry={currentLaundry}
        />
      )}

      {applied && isControlDiario && (
        <ControlDiarioContent
          controlDiario={controlDiario}
          laundry={currentLaundry}
          year={applied.year}
          month={applied.month}
        />
      )}

      {applied && isConsolidadoAnual && (
        <ConsolidadoAnualContent
          consolidados={consolidadosAnio}
          laundry={currentLaundry}
          year={applied.year}
        />
      )}

      {applied && !REPORT_IDS_IMPLEMENTADOS.includes(applied.reportId) && (
        <Card className="rounded-xl border border-gray-200/80 dark:border-gray-700/80 bg-white dark:bg-gray-800 shadow-sm">
          <CardContent className="p-8 text-center">
            {Icon && <Icon className="h-12 w-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />}
            <h3 className="text-base font-bold text-gray-700 dark:text-gray-200 mb-1">
              {selectedReport?.label}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Este reporte está en construcción. Estará disponible próximamente.
            </p>
          </CardContent>
        </Card>
      )}

      {applied && isLotesVsCiclos && (
        <SendReportDialog
          open={sendDialogOpen}
          onOpenChange={setSendDialogOpen}
          correosReporteRaw={currentLaundry?.correosReporte}
          idLavanderia={applied.idLavanderia}
          startDate={applied.startDate}
          endDate={applied.endDate}
          laundryNombre={currentLaundry?.nombre}
        />
      )}
    </div>
  );
};

export default Reportes360Page;
